import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';

interface LocalStorageState {
	// Auth data
	authToken: string | null;
	userProfile: any;
	userLanguage: any;
	notificationLanguage: any;
	loged: boolean;
	profileSelected: any;

	// Location data
	stateId: any;
	districtId: any;
	stateName: any;
	districtName: any;
	lat: any;
	long: any;
	pinCode: any;
	talukId: any;
	ipAddress: any;

	// App state
	refreshPage: any;
	navBarRemoved: any;
	appLocationPermission: any;
	appRoutesData: any;
	lastClearedDate: any;
	first_launch: any;
	activeLang: any;
	bahiKhataGuide: any;

	// Trade specific
	reportDetails: any;
	insightParams: any;
	loadingLocation: any;
	unloadingLocation: any;
	polylineString: any;
	distance: any;
	selectedTab: any;
	is_logged_in_via_report_link: any;
	mobileNumber: any;

	// Actions - Auth
	setAuthToken: (token: string | null) => void;
	setUserProfile: (profile: any) => void;
	setUserLanguage: (language: any) => void;
	setNotificationLanguage: (language: any) => void;
	setLoged: (loged: boolean) => void;
	setProfileSelected: (profile: any) => void;

	// Actions - Location
	setStateId: (stateId: any) => void;
	setDistrictId: (districtId: any) => void;
	setStateName: (stateName: any) => void;
	setDistrictName: (districtName: any) => void;
	setLat: (lat: any) => void;
	setLong: (long: any) => void;
	setPinCode: (pinCode: any) => void;
	setTalukId: (talukId: any) => void;
	setIpAddress: (ipAddress: any) => void;

	// Actions - App state
	setRefreshPage: (refreshPage: any) => void;
	setNavBarRemoved: (navBarRemoved: any) => void;
	setAppLocationPermission: (permission: any) => void;
	setAppRoutesData: (data: any) => void;
	setLastClearedDate: (date: any) => void;
	setFirstLaunch: (firstLaunch: any) => void;
	setActiveLang: (lang: any) => void;
	setBahiKhataGuide: (guide: any) => void;

	// Actions - Trade specific
	setReportDetails: (details: any) => void;
	setInsightParams: (params: any) => void;
	setLoadingLocation: (location: any) => void;
	setUnloadingLocation: (location: any) => void;
	setPolylineString: (polyline: any) => void;
	setDistance: (distance: any) => void;
	setSelectedTab: (tab: any) => void;
	setIsLoggedInViaReportLink: (isLogged: any) => void;
	setMobileNumber: (number: any) => void;

	// Batch operations
	setAuthData: (token: string | null, profile: any, language: any) => void;
	clearAuthData: () => void;
	setLocationData: (locationData: {
		stateId?: any;
		districtId?: any;
		stateName?: any;
		districtName?: any;
		lat?: any;
		long?: any;
		pinCode?: any;
		talukId?: any;
		ipAddress?: any;
	}) => void;
	clearLocationData: () => void;
	clearAll: () => void;
}

export const useLocalStorageStore = create<LocalStorageState>()(
	devtools(
		persist(
			set => ({
				// Initial state - Auth data
				authToken: null,
				userProfile: null,
				userLanguage: null,
				notificationLanguage: null,
				loged: false,
				profileSelected: null,

				// Initial state - Location data
				stateId: null,
				districtId: null,
				stateName: null,
				districtName: null,
				lat: null,
				long: null,
				pinCode: null,
				talukId: null,
				ipAddress: null,

				// Initial state - App state
				refreshPage: 'false',
				navBarRemoved: 'false',
				appLocationPermission: null,
				appRoutesData: null,
				lastClearedDate: null,
				first_launch: 'false',
				activeLang: null,
				bahiKhataGuide: 'false',

				// Initial state - Trade specific
				reportDetails: null,
				insightParams: null,
				loadingLocation: null,
				unloadingLocation: null,
				polylineString: null,
				distance: null,
				selectedTab: null,
				is_logged_in_via_report_link: null,
				mobileNumber: null,

				// Actions - Auth
				setAuthToken: token => set({ authToken: token }),
				setUserProfile: profile => set({ userProfile: profile }),
				setUserLanguage: language => set({ userLanguage: language }),
				setNotificationLanguage: language => set({ notificationLanguage: language }),
				setLoged: loged => set({ loged }),
				setProfileSelected: profile => set({ profileSelected: profile }),

				// Actions - Location
				setStateId: stateId => set({ stateId }),
				setDistrictId: districtId => set({ districtId }),
				setStateName: stateName => set({ stateName }),
				setDistrictName: districtName => set({ districtName }),
				setLat: lat => set({ lat }),
				setLong: long => set({ long }),
				setPinCode: pinCode => set({ pinCode }),
				setTalukId: talukId => set({ talukId }),
				setIpAddress: ipAddress => set({ ipAddress }),

				// Actions - App state
				setRefreshPage: refreshPage => set({ refreshPage }),
				setNavBarRemoved: navBarRemoved => set({ navBarRemoved }),
				setAppLocationPermission: permission =>
					set({ appLocationPermission: permission }),
				setAppRoutesData: data => set({ appRoutesData: data }),
				setLastClearedDate: date => set({ lastClearedDate: date }),
				setFirstLaunch: firstLaunch => set({ first_launch: firstLaunch }),
				setActiveLang: lang => set({ activeLang: lang }),
				setBahiKhataGuide: guide => set({ bahiKhataGuide: guide }),

				// Actions - Trade specific
				setReportDetails: details => set({ reportDetails: details }),
				setInsightParams: params => set({ insightParams: params }),
				setLoadingLocation: location => set({ loadingLocation: location }),
				setUnloadingLocation: location => set({ unloadingLocation: location }),
				setPolylineString: polyline => set({ polylineString: polyline }),
				setDistance: distance => set({ distance }),
				setSelectedTab: tab => set({ selectedTab: tab }),
				setIsLoggedInViaReportLink: isLogged =>
					set({ is_logged_in_via_report_link: isLogged }),
				setMobileNumber: number => set({ mobileNumber: number }),

				// Batch operations
				setAuthData: (token, profile, language) =>
					set({
						authToken: token,
						userProfile: profile,
						userLanguage: language,
						loged: true,
					}),

				clearAuthData: () =>
					set({
						authToken: null,
						userProfile: null,
						userLanguage: null,
						notificationLanguage: null,
						loged: false,
						profileSelected: null,
					}),

				setLocationData: locationData =>
					set(state => ({ ...state, ...locationData })),

				clearLocationData: () =>
					set({
						stateId: null,
						districtId: null,
						stateName: null,
						districtName: null,
						lat: null,
						long: null,
						pinCode: null,
						talukId: null,
						ipAddress: null,
					}),

				clearAll: () =>
					set({
						// Reset to initial values
						authToken: null,
						userProfile: null,
						userLanguage: null,
						notificationLanguage: null,
						loged: false,
						profileSelected: null,
						stateId: null,
						districtId: null,
						stateName: null,
						districtName: null,
						lat: null,
						long: null,
						pinCode: null,
						talukId: null,
						ipAddress: null,
						refreshPage: 'false',
						navBarRemoved: 'false',
						appLocationPermission: null,
						appRoutesData: null,
						lastClearedDate: null,
						first_launch: 'false',
						activeLang: null,
						reportDetails: null,
						insightParams: null,
						loadingLocation: null,
						unloadingLocation: null,
						polylineString: null,
						distance: null,
						selectedTab: null,
						is_logged_in_via_report_link: null,
						mobileNumber: null,
					}),
			}),
			{
				name: 'localStorage-store',
				partialize: state => ({
					// Only persist the data, not the methods
					authToken: state.authToken,
					userProfile: state.userProfile,
					userLanguage: state.userLanguage,
					notificationLanguage: state.notificationLanguage,
					loged: state.loged,
					profileSelected: state.profileSelected,
					stateId: state.stateId,
					districtId: state.districtId,
					stateName: state.stateName,
					districtName: state.districtName,
					lat: state.lat,
					long: state.long,
					pinCode: state.pinCode,
					talukId: state.talukId,
					ipAddress: state.ipAddress,
					refreshPage: state.refreshPage,
					navBarRemoved: state.navBarRemoved,
					appLocationPermission: state.appLocationPermission,
					appRoutesData: state.appRoutesData,
					lastClearedDate: state.lastClearedDate,
					first_launch: state.first_launch,
					activeLang: state.activeLang,
					reportDetails: state.reportDetails,
					insightParams: state.insightParams,
					loadingLocation: state.loadingLocation,
					unloadingLocation: state.unloadingLocation,
					polylineString: state.polylineString,
					distance: state.distance,
					selectedTab: state.selectedTab,
					is_logged_in_via_report_link: state.is_logged_in_via_report_link,
					mobileNumber: state.mobileNumber,
				}),
			},
		),
		{ name: 'localStorage-store' },
	),
);

// Convenience hooks for common data access patterns
export const useAuthData = () => {
	const store = useLocalStorageStore();
	return {
		authToken: store.authToken,
		userProfile: store.userProfile,
		userLanguage: store.userLanguage,
		isLogged: store.loged === true,
		setAuthData: store.setAuthData,
		clearAuthData: store.clearAuthData,
		setAuthToken: store.setAuthToken,
		setUserProfile: store.setUserProfile,
		setUserLanguage: store.setUserLanguage,
	};
};

export const useLocationData = () => {
	const store = useLocalStorageStore();
	return {
		stateId: store.stateId,
		districtId: store.districtId,
		stateName: store.stateName,
		districtName: store.districtName,
		lat: store.lat,
		long: store.long,
		pinCode: store.pinCode,
		talukId: store.talukId,
		ipAddress: store.ipAddress,
		setLocationData: store.setLocationData,
		clearLocationData: store.clearLocationData,
		setStateId: store.setStateId,
		setDistrictId: store.setDistrictId,
		setStateName: store.setStateName,
		setDistrictName: store.setDistrictName,
		setLat: store.setLat,
		setLong: store.setLong,
	};
};

export const useAppState = () => {
	const store = useLocalStorageStore();
	return {
		refreshPage: store.refreshPage,
		navBarRemoved: store.navBarRemoved,
		appLocationPermission: store.appLocationPermission,
		first_launch: store.first_launch,
		activeLang: store.activeLang,
		setRefreshPage: store.setRefreshPage,
		setNavBarRemoved: store.setNavBarRemoved,
		setAppLocationPermission: store.setAppLocationPermission,
		setFirstLaunch: store.setFirstLaunch,
		setActiveLang: store.setActiveLang,
	};
};
